import { combineSlices, createSelector } from '@reduxjs/toolkit';
import type { AppState } from '@src/providers/AppStateProvider/types';
import { apiKeysSliceReducer, isApiKeyAccountFiltersTabDirty, isApiKeysTabDirty } from './apiKeys/apiKeysSlice';
import { drawerSliceReducer } from './drawer/drawerSlice';
import { isPermissionsTabDirty, permissionsSliceReducer } from './permissions/permissionsSlice';
import { userDetailsSliceReducer } from './userDetails/userDetailsSlice';

export const customerUserSlice = combineSlices({
  drawer: drawerSliceReducer,
  userDetails: userDetailsSliceReducer,
  apiKeys: apiKeysSliceReducer,
  permissions: permissionsSliceReducer,
});

export interface TabDef {
  label: 'General' | 'Account Restrictions' | 'API Keys' | 'API Key Accounts';
  isDirty: boolean;
}
export interface TabDefs {
  general: TabDef;
  permissions: TabDef;
  apiKeys: TabDef;
  apiKeyAccounts: TabDef;
}

export const selectTabDefs = createSelector(
  (state: AppState) => state.customerUserDrawer,
  (state: AppState, enableCustomerAccountRestrictions: boolean) => enableCustomerAccountRestrictions,
  (customerUserDrawer, enableCustomerAccountRestrictions): TabDef[] => {
    const tabDefs: TabDef[] = [
      {
        label: 'General',
        isDirty: customerUserDrawer.userDetails.isDirty,
      },
    ];

    if (enableCustomerAccountRestrictions) {
      tabDefs.push({
        label: 'Account Restrictions',
        isDirty: isPermissionsTabDirty(customerUserDrawer.permissions),
      });
    }

    if (customerUserDrawer.userDetails.customerUserId) {
      tabDefs.push({
        label: 'API Keys',
        isDirty: isApiKeysTabDirty(customerUserDrawer.apiKeys),
      });
      tabDefs.push({
        label: 'API Key Accounts',
        isDirty: isApiKeyAccountFiltersTabDirty(customerUserDrawer.apiKeys),
      });
    }

    return tabDefs;
  }
);

export const selectIsDrawerDirty = (state: AppState, enableCustomerAccountRestrictions: boolean) => {
  return selectTabDefs(state, enableCustomerAccountRestrictions).some(tabDef => tabDef.isDirty);
};
